/**
 * TeamSettingsTab Component
 *
 * Main team management tab for workspace settings.
 * Combines team overview, members list, pending invites, and invite functionality.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  SettingsWell,
  SettingsListCard,
  SettingsList,
  SettingsListRow,
  settingsButtonClass,
} from '@/components/shared/settings/SettingsKit'
import { useWorkspaceTeam } from '@/lib/contexts/WorkspaceTeamContext'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { usePlanUsage } from '@/hooks/usePlanUsage'
import { PlanLimitBanner, buildPlanLimitCopy } from '@/components/shared/plan-usage'
import { getPlanDisplayName } from '@/lib/constants/pricing'
import WorkspaceMembersList from './WorkspaceMembersList'
import InviteToWorkspaceModal from './InviteToWorkspaceModal'
import type { WorkspaceInvite } from '@/lib/types/workspace-team'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'

export default function TeamSettingsTab() {
  const { user } = useAuth()
  const { handleUpgrade, currentPlan, planName } = useSubscription()
  const { workspace, properties } = useWorkspace()
  const {
    members,
    invites,
    isLoading,
    isOwner,
    hasTeamAccess,
    canInvite,
    inviteMember,
    removeMember,
    updateRole,
    resendInvite,
    cancelInvite,
  } = useWorkspaceTeam()

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null)

  // Seat counts/copy come from the shared plan-usage endpoint (source of truth)
  // instead of the local `features.teamSeats` math. Seats are account-wide, so
  // any property in the workspace resolves the same limit. The local members +
  // pending-invites count feeds the fallback so the meter/banner stay correct
  // when the endpoint can't answer — otherwise Invite would sit disabled with
  // no explanation.
  const { teamSeats: teamSeatsUsage, metadata: planUsageMetadata } = usePlanUsage({
    propertyId: properties[0]?.propertyId,
    fallbackCounts: { teamSeats: members.length + invites.length },
  })
  const displayPlanName = getPlanDisplayName(currentPlan, planUsageMetadata?.planLabel || planName)
  const seatLimitCopy = buildPlanLimitCopy({
    noun: 'team members',
    limit: teamSeatsUsage.limit,
    planName: displayPlanName,
    resolution: 'Remove a member below, or upgrade for more.',
  })

  // Both gates, because they answer different questions and either one alone
  // has been wrong before: `canInvite` covers role + team access, the usage
  // counter covers plan limits and the read-only-after-cancel policy. Whenever
  // this is false the PlanLimitBanner below explains why and offers Upgrade.
  const inviteBlocked = !canInvite || !teamSeatsUsage.canAdd

  const handleResendInvite = async (invite: WorkspaceInvite) => {
    setProcessingInviteId(invite.invite_id)
    try {
      await resendInvite(invite.invite_id)
    } finally {
      setProcessingInviteId(null)
    }
  }

  const handleCancelInvite = async (invite: WorkspaceInvite) => {
    setProcessingInviteId(invite.invite_id)
    try {
      await cancelInvite(invite.invite_id)
    } finally {
      setProcessingInviteId(null)
    }
  }

  if (!hasTeamAccess) {
    return (
      <SettingsWell>
        <SettingsListCard
          title="Team"
          description="Team collaboration is on the Scale plan. Upgrade to invite members and manage access."
          action={
            <Button className={settingsButtonClass} onClick={() => void handleUpgrade('manual')}>
              Upgrade to Scale
            </Button>
          }
        />
      </SettingsWell>
    )
  }

  if (isLoading) {
    return (
      <SettingsWell>
        <SettingsListCard title={<Skeleton className="h-4 w-28" />} description={<Skeleton className="mt-1 h-3 w-44" />}>
          <Skeleton className="h-[52px] w-full" />
          <Skeleton className="h-[52px] w-full" />
        </SettingsListCard>
      </SettingsWell>
    )
  }

  const seats = teamSeatsUsage.isUnlimited
    ? 'Unlimited seats'
    : `${teamSeatsUsage.current} of ${teamSeatsUsage.limit} seats used`

  return (
    <SettingsWell>
      <SettingsListCard
        title="Members"
        description={`${seats} · access to ${properties.length} site${properties.length !== 1 ? 's' : ''}`}
        action={
          isOwner && (
            <Button
              variant="outline"
              onClick={() => setIsInviteModalOpen(true)}
              disabled={inviteBlocked}
              className={settingsButtonClass}
            >
              Invite member
            </Button>
          )
        }
      >
        {!teamSeatsUsage.isUnlimited && (
          <Progress
            value={(teamSeatsUsage.current / teamSeatsUsage.limit) * 100}
            aria-label={seats}
            className="h-1 bg-canvas [&>div]:bg-foreground"
          />
        )}

        {!teamSeatsUsage.canAdd && (
          <PlanLimitBanner
            title={seatLimitCopy.title}
            description={seatLimitCopy.description}
            noun="team members"
            planName={displayPlanName}
            isOwner={planUsageMetadata?.isOwner ?? isOwner}
          />
        )}

        {members.length === 0 && invites.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] leading-5 text-foreground/60">
            No team members yet. Invite collaborators to share access to every site in this workspace.
          </p>
        ) : (
          <WorkspaceMembersList
            members={members}
            isOwner={isOwner}
            currentUserEmail={user?.email}
            onRemoveMember={removeMember}
            onUpdateRole={updateRole}
          />
        )}
      </SettingsListCard>

      {invites.length > 0 && (
        <SettingsListCard
          title={
            <>
              Pending invites
              <span className="text-xs font-medium tabular-nums text-foreground/60">{invites.length}</span>
            </>
          }
          description="They join when they accept the email."
        >
          <SettingsList>
            {invites.map((invite) => {
              const busy = processingInviteId === invite.invite_id
              return (
                <SettingsListRow
                  key={invite.invite_id}
                  primary={invite.invitee_email}
                  secondary={`Sent ${new Date(invite.created_at).toLocaleDateString()}`}
                  trailing={
                    isOwner && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleResendInvite(invite)}
                          disabled={busy}
                          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                        >
                          {busy && <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />}
                          Resend
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelInvite(invite)}
                          disabled={busy}
                          className="h-7 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </>
                    )
                  }
                />
              )
            })}
          </SettingsList>
        </SettingsListCard>
      )}

      <InviteToWorkspaceModal
        open={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
        workspaceName={workspace?.name || 'Workspace'}
        propertyCount={properties.length}
        onInvite={inviteMember}
        canInvite={!inviteBlocked}
        seatLimitReached={!teamSeatsUsage.canAdd}
        availableSeats={
          teamSeatsUsage.isUnlimited ? 'Unlimited' : Math.max(0, teamSeatsUsage.limit - teamSeatsUsage.current)
        }
      />
    </SettingsWell>
  )
}
